import React, { useCallback, useEffect, useState } from 'react';
import SafeIcon from '../../components/common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { GFI_ROLE_OPTIONS, getRoleLabel } from '../../constants/roles';

export default function UserManagementPage() {
  const { can, setUserRole, resolveAgentCode, refreshProfile } = useAuth();
  const canManageRoles = can('manage_users');
  const canViewConflicts = can('view_agent_conflicts');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resolveInputs, setResolveInputs] = useState({});

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data, error: fetchError } = await supabase
      .from('gfi_profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (fetchError) {
      setError(fetchError.message);
      setUsers([]);
    } else {
      setUsers(data ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (canViewConflicts) loadUsers();
  }, [canViewConflicts, loadUsers]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await setUserRole(userId, newRole);
      await loadUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleResolveCode = async (userId) => {
    const newCode = resolveInputs[userId]?.trim();
    if (!newCode) return;
    try {
      await resolveAgentCode(userId, newCode);
      setResolveInputs((prev) => ({ ...prev, [userId]: '' }));
      await loadUsers();
      await refreshProfile();
    } catch (err) {
      alert(err.message);
    }
  };

  if (!canViewConflicts) {
    return null;
  }

  const conflictCount = users.filter((u) => u.agent_code_conflict).length;

  return (
    <div className="min-h-screen bg-[var(--bg-base)] pb-24 md:pb-12">
      <div className="bg-[var(--bg-surface)] border-b border-[var(--border-subtle)] pt-12 pb-8 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-black text-[var(--text-main)] uppercase italic tracking-tight mb-2">
            Gestión de Usuarios
          </h1>
          <p className="text-xs text-[var(--text-secondary)] uppercase tracking-widest">
            {canManageRoles
              ? 'Superadmin — roles y conflictos de código de agente'
              : 'Admin — revisión de conflictos de código de agente'}
          </p>
          {conflictCount > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-brand-gold/10 border border-brand-gold/30 rounded-xl">
              <SafeIcon name="AlertTriangle" className="w-4 h-4 text-brand-gold-dark" />
              <span className="text-[10px] font-black text-brand-gold-dark uppercase tracking-widest">
                {conflictCount} conflicto{conflictCount !== 1 ? 's' : ''} pendiente{conflictCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {loading ? (
          <p className="text-sm text-[var(--text-secondary)]">Cargando usuarios...</p>
        ) : error ? (
          <p className="text-sm text-rose-600">{error}</p>
        ) : (
          <div className="space-y-4">
            {users.map((user) => (
              <div
                key={user.id}
                className={`bg-[var(--bg-surface)] border rounded-2xl p-5 md:p-6 ${
                  user.agent_code_conflict
                    ? 'border-brand-gold/40 shadow-gold-glow'
                    : 'border-[var(--border-subtle)]'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h2 className="font-black text-[var(--text-main)] uppercase tracking-tight">
                        {user.full_name || 'Sin nombre'}
                      </h2>
                      {user.agent_code_conflict && (
                        <span className="px-2 py-0.5 bg-brand-gold text-brand-navy text-[8px] font-black uppercase tracking-widest rounded-full">
                          Conflicto de código
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--text-secondary)] mb-2">{user.email}</p>
                    <div className="flex flex-wrap gap-3 text-[10px] font-bold uppercase tracking-widest text-[var(--text-secondary)]">
                      <span>Rol: {getRoleLabel(user.role)}</span>
                      <span>Código: {user.agent_code}</span>
                      {user.requested_agent_code && user.agent_code_conflict && (
                        <span className="text-brand-gold-dark">
                          Solicitó: {user.requested_agent_code}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                    {canManageRoles && (
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        className="bg-[var(--bg-base)] border border-[var(--border-subtle)] text-[var(--text-main)] text-[10px] font-bold uppercase tracking-widest rounded-xl px-4 py-3 outline-none focus:border-brand-cyan"
                      >
                        {GFI_ROLE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {user.agent_code_conflict && (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Nuevo código"
                          value={resolveInputs[user.id] ?? ''}
                          onChange={(e) =>
                            setResolveInputs((prev) => ({
                              ...prev,
                              [user.id]: e.target.value.toUpperCase(),
                            }))
                          }
                          className="bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl px-3 py-2 text-xs font-bold uppercase w-36 focus:outline-none focus:border-brand-cyan"
                        />
                        <button
                          onClick={() => handleResolveCode(user.id)}
                          className="px-4 py-2 gfi-gradient-bg text-brand-navy rounded-xl text-[10px] font-black uppercase tracking-widest"
                        >
                          Resolver
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
